import type {
  ApiFolder,
  DeleteFolderResponse,
  UpdateFolderRequest,
  UpdateFolderResponse,
} from "@workith/shared";
import { getSharedDriveCapabilities } from "@workith/shared";
import { DropdownMenuItem, DropdownMenuSeparator } from "@workith/ui";
import { Edit, Trash2 } from "lucide-react";
import api from "../../lib/api";
import { RowActionsMenu } from "./RowActionsMenu";

interface FolderActionsProps {
  folder: ApiFolder;
  onUpdate: () => void;
}

function folderErrorMessage(error: unknown, fallback: string): string {
  return error instanceof Error ? error.message : fallback;
}

export function canRenameFolder(folder: ApiFolder): boolean {
  if (folder.root_kind !== "normal") {
    return false;
  }

  if (folder.scope === "shared_drive") {
    return getSharedDriveCapabilities(folder.user_role).canWrite;
  }

  return true;
}

export function canDeleteFolder(folder: ApiFolder): boolean {
  if (folder.root_kind !== "normal") {
    return false;
  }

  if (folder.scope === "shared_drive") {
    return getSharedDriveCapabilities(folder.user_role).canManage;
  }

  return true;
}

export function FolderActions({ folder, onUpdate }: FolderActionsProps) {
  const canRename = canRenameFolder(folder);
  const canDelete = canDeleteFolder(folder);

  const handleRename = async () => {
    const newName = prompt("New folder name:", folder.name);
    if (!newName || newName === folder.name) return;

    const request: UpdateFolderRequest = { name: newName };

    try {
      await api.put<UpdateFolderRequest, UpdateFolderResponse>(
        `/api/folders/${folder.id}`,
        request
      );
      onUpdate();
    } catch (err) {
      console.error("Failed to rename folder:", err);
      alert(folderErrorMessage(err, "Failed to rename folder"));
    }
  };

  const handleDelete = async () => {
    if (
      !confirm(
        `Delete folder "${folder.name}" and all its contents? This will permanently delete all subfolders and documents inside.`
      )
    )
      return;

    try {
      await api.delete<DeleteFolderResponse>(`/api/folders/${folder.id}`);
      onUpdate();
    } catch (err) {
      console.error("Failed to delete folder:", err);
      alert(folderErrorMessage(err, "Failed to delete folder"));
    }
  };

  if (!canRename && !canDelete) {
    return null;
  }

  return (
    <RowActionsMenu>
      {canRename && (
        <DropdownMenuItem onSelect={handleRename}>
          <Edit className="h-4 w-4" />
          Rename
        </DropdownMenuItem>
      )}

      {canDelete && (
        <>
          {canRename && <DropdownMenuSeparator />}
          <DropdownMenuItem variant="destructive" onSelect={handleDelete}>
            <Trash2 className="h-4 w-4" />
            Delete
          </DropdownMenuItem>
        </>
      )}
    </RowActionsMenu>
  );
}
